import type { AudioOnset, ChartAudio, ChartPalette } from '../chart.js';
import { buildVariantTitle } from '../chart.js';
import { type Difficulty, type Variant } from '../gameTypes.js';
import type {
  ImportedCircleBundle,
  ImportedCircleEntry,
  ImportedOnsetsFile,
  PerVariantTimingArg,
} from '../circlesBuiltinMaps/importedOsuChart.js';
import { withFillerCircleEntries } from '../circlesBuiltinMaps/importedOsuChart.js';
import {
  ceilingYAt,
  floorYAt,
  terrainWalls,
  WAVES_DEFAULT_SPAWN_Y,
  WAVES_VARIANT_TUNING,
  type WavesChart,
  type WavesGate,
  type WavesObstacle,
  type WavesTerrain,
  type WavesTerrainNode,
} from '../wavesChart.js';
import { buildWavesEngineConfig } from '../engine/waves/state.js';
import { planSegmentInputs } from '../engine/waves/headlessRunnerPlanSegment.js';
import {
  feasibleCentreRange,
  feasibleWallRange,
  groupHazardsByBeat,
  intersectRanges,
  PLANNER_EDGE_MARGIN,
  reachableYAtBeat,
  type CentreRange,
} from '../engine/waves/reachability.js';
import { PALETTE_AURORA } from '../builtinPulseMaps/palettes.js';
import { OSU_PLAYFIELD_HEIGHT } from '../circlesChart.js';
import { hashStringToSeed, mulberry32 } from '../seededRng.js';
import {
  generateWavesTerrain,
  wallApproachBeats,
  wavesIntensityAt,
  WAVES_TERRAIN_CADENCE_BEATS,
  WAVES_WALL_RECOVERY_BEATS,
} from './wavesTerrain.js';

/**
 * Halfway line of the osu playfield in y. Circles above this line
 * become ceiling spikes (forcing the player to drop), circles below
 * become floor spikes (forcing them to climb). The plan's spatial
 * mapping rule, lifted directly from the source map's authored
 * vertical placement.
 */
const OSU_HALF_Y = OSU_PLAYFIELD_HEIGHT / 2;

/**
 * Slider tunnel-gate spacing (in beats), per variant. Sliders span
 * [headBeat, endBeat] — the deriver emits a centred tunnel gate
 * every step through the span so the player has to actively maintain
 * corridor centre rather than just thread one gate at the head.
 *
 * Per-variant so a long slider in a casual Easy/Normal source doesn't
 * spam more gates than the Hard variant's tighter mix (which would
 * invert the difficulty curve). Tighter step = more gates = more
 * precision demanded.
 */
const SLIDER_TUNNEL_BEAT_STEP: Record<Variant, number> = {
  normal: 1.5,
  hard: 1.0,
  extreme: 0.5,
};

/**
 * Spinner gate spacing (in beats), per variant. Same scaling
 * rationale as slider step — spinners get the tightest extreme
 * step because they read as the chart's "cosmic vortex" climax and
 * mechanically the most demanding stretch.
 */
const SPINNER_TUNNEL_BEAT_STEP: Record<Variant, number> = {
  normal: 1.5,
  hard: 1.0,
  extreme: 0.5,
};

/**
 * Minimum beat gap between two consecutive obstacles on OPPOSING
 * sides. Variant-uniform: any per-variant escalation would invert
 * the difficulty curve (harder variants would drop more obstacles,
 * leaving Extreme lighter than Hard).
 *
 * Just large enough to suppress engine-noise micro-overlap into
 * visually-merged hazards. The real "actually clearable" guarantee
 * lives in {@link filterUnreachableObstacles} (same planner the
 * perfect-replay tests use). Difficulty differentiation comes from
 * {@link WAVES_VARIANT_TUNING} (faster vY, smaller radius, tighter
 * gap height) and the per-variant slider/spinner gate density above.
 */
const OPPOSING_MIN_BEAT_GAP = 0.15;

/**
 * Same-side density cap: spikes on the same surface within this many
 * beats collapse into one. Two floor spikes 1/16 beat apart visually
 * read as one chunky hazard but the engine would award two
 * resolutions, which over-rewards combo. The 0.1 floor matches the
 * narrowest "double-tap" beat spacing osu! mappers actually charts.
 */
const SAME_SIDE_MIN_BEAT_GAP = 0.1;

/**
 * Tight-tunnel gap height multiplier for sliders. Multiplied by the
 * variant's `minGapHeight` to give the actual gap size — slider
 * tunnels are slightly tighter than spike safe bands because the
 * mapper authored that stretch as "you should be tracking this", so
 * the corridor reads as a path rather than a corridor with room to
 * spare.
 */
const SLIDER_GAP_MUL = 0.9;

/** Spinner gap multiplier — tightest band on the playfield. */
const SPINNER_GAP_MUL = 0.7;

/** Clamp helper (no shared util in this module). */
function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

interface WavesSawTuning {
  /** Chance an eligible spike (past the spacing gate) is reforged as a saw. */
  convertProb: number;
  /** Minimum beats between two saws, so a run reads as blades not noise. */
  minSpacingBeats: number;
  /** Saw radius as a fraction of the LOCAL corridor height (adapts to squeeze). */
  radiusFrac: number;
  minRadius: number;
  maxRadius: number;
  /**
   * Beat-keyed oscillation: a run of saws bites deeper / shallower in a
   * sine rhythm. Sampled at the (fixed) hazard beat, so the position
   * bakes into the chart and stays replay-deterministic — no runtime RNG
   * or per-frame drift. `amplitude` is the max extra protrusion (px) into
   * the open lane; `freq` is the angular speed in radians per beat.
   */
  oscAmplitude: number;
  oscFreq: number;
  /**
   * Chance an eligible saw floats CENTRE-corridor ("pick-a-side") instead
   * of mounting flush to a surface — but only where the LOCAL corridor is
   * wide enough to leave a roundable lane on BOTH sides (see
   * {@link injectSaws}). A central blade is the iconic Geometry-Dash–wave
   * "commit above or below the spinning disc" beat: the reachability
   * planner threads whichever lane is wider, so it's always clearable.
   * Higher on harder tiers, where the wider commitment reads as a
   * climax moment.
   */
  centralProb: number;
}

/**
 * Per-variant saw mix. A deterministic subset of spike gates is reforged
 * into saws (see {@link injectSaws}) — the lowest-effort new hazard since
 * the type, collision, and renderer already exist. A mounted saw keeps the
 * spike's pin direction (a floor saw still forces a climb) so it intermixes
 * naturally with the surviving spikes; a CENTRAL saw (rolled at
 * `centralProb` where the corridor is wide enough) floats mid-corridor as a
 * pick-a-side commitment. Harder tiers convert a larger, tighter, more
 * deeply-oscillating share and float more of them centrally.
 */
const WAVES_SAW_TUNING: Record<Variant, WavesSawTuning> = {
  normal: { convertProb: 0.22, minSpacingBeats: 6, radiusFrac: 0.26, minRadius: 26, maxRadius: 44, oscAmplitude: 24, oscFreq: 1.1, centralProb: 0.35 },
  hard: { convertProb: 0.28, minSpacingBeats: 5, radiusFrac: 0.28, minRadius: 24, maxRadius: 46, oscAmplitude: 30, oscFreq: 1.25, centralProb: 0.45 },
  extreme: { convertProb: 0.34, minSpacingBeats: 4, radiusFrac: 0.3, minRadius: 22, maxRadius: 48, oscAmplitude: 36, oscFreq: 1.4, centralProb: 0.55 },
};

/**
 * Minimum clear lane (playfield units) the player's bounding circle must
 * keep on the open side of a saw, on top of its own diameter. Below this
 * the local corridor is too tight (e.g. mid-squeeze) to round the blade,
 * so the spike is left as-is rather than reforged into an unfair saw.
 */
export const WAVES_SAW_MIN_OPEN_CLEARANCE = 30;

/**
 * Drop obstacles inside any wall's approach/recovery window so each wall
 * keeps the clear run-up it needs to stay reachable (the larger
 * approach) plus a thin post-wall pad (the smaller recovery). Walls are
 * sparse, so this only thins obstacles right around them.
 */
function dropObstaclesNearWalls(
  obstacles: ReadonlyArray<WavesObstacle>,
  terrain: WavesTerrain,
  vY: number,
): WavesObstacle[] {
  const walls = terrainWalls(terrain);
  if (walls.length === 0) {
    return [...obstacles];
  }
  const approach = wallApproachBeats(vY);
  return obstacles.filter(
    (o) =>
      !walls.some(
        (w) => o.beat > w.beat - approach && o.beat < w.beat + WAVES_WALL_RECOVERY_BEATS,
      ),
  );
}

/**
 * Re-express onsets stored in the anchor variant's beat space into a
 * different variant's beat space. Mirrors `rescaleOnsets` in
 * `importedOsuChart.ts` — kept private here so Waves doesn't have to
 * export a circles-internal helper just for one call-site.
 */
function rescaleOnsets(
  anchorBpm: number,
  anchorOffsetMs: number,
  onsets: ReadonlyArray<{ beat: number; kind: string; intensity: number }>,
  toBpm: number,
  toOffsetMs: number,
): AudioOnset[] {
  const out: AudioOnset[] = [];
  for (const o of onsets) {
    const realMs = (o.beat * 60000) / anchorBpm + anchorOffsetMs;
    if (realMs < toOffsetMs) {
      continue;
    }
    const beat = ((realMs - toOffsetMs) * toBpm) / 60000;
    const kind: AudioOnset['kind'] =
      o.kind === 'kick' || o.kind === 'snare' ? o.kind : 'lead';
    out.push({
      beat: Math.round(beat * 1000) / 1000,
      kind,
      intensity: o.intensity,
    });
  }
  return out;
}

type SpikeSide = 'floor' | 'ceiling';

interface NormalisedHit {
  beat: number;
  x: number;
  y: number;
  kind: 'circle' | 'slider' | 'spinner';
  endBeat?: number;
}

/**
 * Map an osu hit object's `(y)` to the spike side this Waves derivation
 * places. osu top half (y < OSU_HALF_Y) → ceiling spike → safe band at
 * the bottom of the playfield (player drops to dodge).
 */
function spikeSideFromOsuY(y: number): SpikeSide {
  return y < OSU_HALF_Y ? 'ceiling' : 'floor';
}

/**
 * Build the gate geometry for a single spike, anchored to the LOCAL
 * terrain surface so the spike sits on the winding corridor wall (not
 * the flat playfield edge), with the variant's tuned safe-band height:
 *
 *   - floor spike: safe band at the TOP of the corridor — gap from the
 *     local ceiling down `minGapHeight`; the spike fills the rest down
 *     to the local floor (forcing a climb).
 *   - ceiling spike: safe band at the BOTTOM — gap up `minGapHeight`
 *     from the local floor (forcing a dive).
 */
function makeSpikeGate(
  id: string,
  beat: number,
  side: SpikeSide,
  variant: Variant,
  terrain: WavesTerrain,
): WavesGate {
  const minGap = WAVES_VARIANT_TUNING[variant].minGapHeight;
  const top = ceilingYAt(terrain, beat);
  const bottom = floorYAt(terrain, beat);
  // Clamp the safe band to the LOCAL corridor. Where the corridor has
  // squeezed narrower than `minGap`, the band fills the whole corridor
  // (the spike's lethal pillar shrinks to nothing) instead of spilling
  // past the floor / ceiling and out of the playfield — the squeeze is
  // already the hazard there.
  if (side === 'floor') {
    return {
      id,
      kind: 'gate',
      beat,
      gapTopY: top,
      gapBottomY: Math.min(top + minGap, bottom),
      visual: 'spike-floor',
    };
  }
  return {
    id,
    kind: 'gate',
    beat,
    gapTopY: Math.max(bottom - minGap, top),
    gapBottomY: bottom,
    visual: 'spike-ceiling',
  };
}

/**
 * Centred tunnel gate, anchored to the LOCAL corridor midline — the
 * player rides the middle of the winding corridor rather than the flat
 * playfield centre.
 */
function makeTunnelGate(
  id: string,
  beat: number,
  gapMul: number,
  variant: Variant,
  terrain: WavesTerrain,
): WavesGate {
  const gapH = WAVES_VARIANT_TUNING[variant].minGapHeight * gapMul;
  const mid = (ceilingYAt(terrain, beat) + floorYAt(terrain, beat)) / 2;
  return {
    id,
    kind: 'gate',
    beat,
    gapTopY: mid - gapH / 2,
    gapBottomY: mid + gapH / 2,
    visual: 'pillar',
  };
}

/**
 * Translate ImportedCircleEntry records into the Waves deriver's
 * normalised shape. Drops slider/spinner entries without their
 * required end-beat metadata; these would otherwise produce zero-
 * length spans during obstacle generation.
 */
function normaliseHits(circles: ReadonlyArray<ImportedCircleEntry>): NormalisedHit[] {
  const out: NormalisedHit[] = [];
  for (const c of circles) {
    if (c.kind === 'spinner') {
      if (c.spinnerEndBeat === undefined || c.spinnerEndBeat <= c.beat) {
        continue;
      }
      out.push({
        beat: c.beat,
        x: c.x,
        y: c.y,
        kind: 'spinner',
        endBeat: c.spinnerEndBeat,
      });
      continue;
    }
    if (c.kind === 'slider') {
      if (!c.slider || c.slider.endBeat <= c.beat) {
        // Slider without geometry collapses to a plain circle; sane
        // since the validator-side circles importer does the same.
        out.push({ beat: c.beat, x: c.x, y: c.y, kind: 'circle' });
        continue;
      }
      out.push({
        beat: c.beat,
        x: c.x,
        y: c.y,
        kind: 'slider',
        endBeat: c.slider.endBeat,
      });
      continue;
    }
    out.push({ beat: c.beat, x: c.x, y: c.y, kind: 'circle' });
  }
  return out;
}

/**
 * Drop hits that would produce duplicate or visually-merged
 * resolutions. Walks the (already sorted-by-beat) list once and
 * keeps the densest schedule consistent with:
 *   - opposing-side spikes spaced at least {@link OPPOSING_MIN_BEAT_GAP}
 *     beats apart;
 *   - same-side spikes never closer than {@link SAME_SIDE_MIN_BEAT_GAP}.
 *
 * The returned list is the chart's spike skeleton; sliders / spinners
 * pass through unchanged (they're handled separately). Variant-
 * uniform on purpose — see {@link OPPOSING_MIN_BEAT_GAP} doc.
 * "Actually clearable" is enforced downstream by
 * {@link filterUnreachableObstacles}.
 */
export function wavesIntensityFilter(
  _variant: Variant,
  hits: ReadonlyArray<NormalisedHit>,
): NormalisedHit[] {
  const out: NormalisedHit[] = [];
  let lastSpikeBeat = -Infinity;
  let lastSpikeSide: SpikeSide | null = null;
  for (const h of hits) {
    if (h.kind !== 'circle') {
      out.push(h);
      continue;
    }
    const side = spikeSideFromOsuY(h.y);
    const gap = h.beat - lastSpikeBeat;
    if (lastSpikeSide === null) {
      out.push(h);
      lastSpikeBeat = h.beat;
      lastSpikeSide = side;
      continue;
    }
    const minGap = side === lastSpikeSide ? SAME_SIDE_MIN_BEAT_GAP : OPPOSING_MIN_BEAT_GAP;
    if (gap < minGap) {
      continue;
    }
    out.push(h);
    lastSpikeBeat = h.beat;
    lastSpikeSide = side;
  }
  return out;
}

/**
 * Stateful reachability pass: drops obstacles the player can't reach
 * from the previous accepted obstacle's chosen Y given the variant's
 * vertical speed. Without this, the per-edge gap floor
 * ({@link OPPOSING_MIN_BEAT_GAP}, {@link SAME_SIDE_MIN_BEAT_GAP})
 * only catches consecutive-spike transitions — spike→tunnel and
 * tunnel→spike crossings can still be infeasible if the prior
 * obstacle pins the player near a wall and the next one demands the
 * opposite wall in a short gap.
 *
 * Uses the same `reachableYAtBeat` + `feasibleCentreRange` helpers
 * the perfect-replay planner uses, so any obstacle this filter
 * accepts is guaranteed to be threadable by the test-time runner.
 * If the two diverged (e.g. different float margins, different
 * sampling), builtin charts could ship with single-pixel
 * infeasibilities that no human can clear.
 *
 * Drops are silent — the derivation is best-effort, and downstream
 * tests cap obstacle count anyway.
 */
function makeFilterStubChart(
  playerVyPerBeat: number,
  playerRadius: number,
  totalBeats: number,
  spawnY: number,
  terrain: WavesTerrain,
): WavesChart {
  return {
    mode: 'waves',
    id: 'filter-stub',
    slug: 'filter-stub',
    title: 'filter-stub',
    description: '',
    bpm: 120,
    totalBeats,
    difficulty: 1,
    variant: 'normal',
    palette: PALETTE_AURORA,
    audio: {
      src: '/test.mp3',
      bpm: 120,
      offsetMs: 0,
      durationSec: 60,
      attribution: 'test',
      onsets: [],
    },
    spawnY,
    playerVyPerBeat,
    playerRadius,
    obstacles: [],
    terrain,
  };
}

/**
 * Drop the PRE node of the duplicate-beat pair at `beat` on one surface,
 * collapsing a lethal vertical step into a glide-able ramp that lands on
 * the SAME downstream Y. Width is preserved (the sibling surface is healed
 * with the identical edit) and the resulting ramp slope stays well under
 * `vY` (wall heights are bounded and the run-in spans a full cadence), so
 * the healed terrain still validates and stays replay-deterministic.
 */
function healSurfaceStep(
  nodes: ReadonlyArray<WavesTerrainNode>,
  beat: number,
): WavesTerrainNode[] {
  for (let i = 0; i + 1 < nodes.length; i += 1) {
    if (Math.abs(nodes[i].beat - beat) < 1e-6 && Math.abs(nodes[i + 1].beat - beat) < 1e-6) {
      return [...nodes.slice(0, i), ...nodes.slice(i + 1)];
    }
  }
  return [...nodes];
}

/**
 * Remove the lethal wall at `beat` from BOTH surfaces (the midline jump
 * stepped one surface into the corridor and the other away from it; both
 * carry a duplicate-beat node), turning the discontinuity into a smooth
 * ramp without shifting any downstream geometry.
 *
 * Exported for direct unit coverage of the heal contract; the generator
 * calls it from {@link filterUnreachableObstacles}.
 */
export function healWallAtBeat(terrain: WavesTerrain, beat: number): WavesTerrain {
  return {
    floor: healSurfaceStep(terrain.floor, beat),
    ceiling: healSurfaceStep(terrain.ceiling, beat),
  };
}

interface ThreadResult {
  kept: WavesObstacle[];
  /** Beat of the first lethal wall the greedy thread couldn't reach, else null. */
  unreachableWallBeat: number | null;
}

/**
 * One greedy single-flip thread through obstacles + lethal walls (the
 * SAME pass the perfect-replay planner runs, so "thread accepts ⇔ planner
 * clears" holds). Obstacles that don't fit the reachable band are dropped;
 * a wall that doesn't fit STOPS the pass and is reported so the caller can
 * heal it — never frozen-and-skipped, which would strand the whole tail
 * (every later wall then reads as unreachable across the un-threaded gap).
 */
function threadObstaclesOnce(
  obstacles: ReadonlyArray<WavesObstacle>,
  terrain: WavesTerrain,
  playerVyPerBeat: number,
  playerRadius: number,
  spawnY: number,
  totalBeats: number,
  healedRampZones: ReadonlyArray<readonly [number, number]>,
): ThreadResult {
  const stub = makeFilterStubChart(playerVyPerBeat, playerRadius, totalBeats, spawnY, terrain);
  const config = buildWavesEngineConfig(stub);

  // Merge obstacles + lethal walls into beat-grouped waypoints. Walls are
  // mandatory terrain (threaded, never dropped) and carry a null item;
  // obstacles are droppable. Same-beat hazards collapse into one group via
  // `groupHazardsByBeat` — the SAME grouping the perfect-replay planner
  // uses — so the filter targets the identical Y per beat the planner
  // would, which is what keeps "filter accepts ⇔ planner clears" true.
  // (Obstacle↔obstacle ties self-resolve here — the loser is dropped and
  // only the survivor ships, so the planner never re-sees the tie. A
  // wall↔obstacle tie can't occur: `dropObstaclesNearWalls` already cleared
  // every obstacle within a wall's runway, so `groupHazardsByBeat`'s
  // `dropped` list only ever carries obstacles and is safe to discard.)
  const entries: Array<{ beat: number; band: CentreRange; item: WavesObstacle | null }> = [];
  for (const obs of obstacles) {
    // Obstacles in a healed wall's run-in ramp were mounted against the
    // PRE-heal surface (`injectSaws` / spike gap-Y hug the local terrain),
    // so the melt shifted that surface out from under them — drop them
    // rather than ship a hazard floating off its mount.
    if (healedRampZones.some(([lo, hi]) => obs.beat > lo && obs.beat < hi)) {
      continue;
    }
    const band = feasibleCentreRange(obs, playerRadius, terrain);
    if (!band) {
      continue;
    }
    entries.push({ beat: obs.beat, band, item: obs });
  }
  for (const wall of config.walls) {
    const band = feasibleWallRange(wall, terrain, playerRadius);
    if (!band) {
      // Corridor too tight to host a gap past this face — heal it away.
      return { kept: [], unreachableWallBeat: wall.beat };
    }
    entries.push({ beat: wall.beat, band, item: null });
  }

  const { groups } = groupHazardsByBeat(entries);

  const out: WavesObstacle[] = [];
  let curY = spawnY;
  let curPressing = false;
  let lastBeat = 0;

  for (const group of groups) {
    const obstaclesInGroup = group.items.filter((o): o is WavesObstacle => o !== null);
    const hasWall = group.items.some((o) => o === null);
    const expanded = reachableYAtBeat(config, stub, lastBeat, group.beat, curY, curPressing);
    const inter = intersectRanges(expanded, group.band);
    if (!inter) {
      if (hasWall) {
        return { kept: out, unreachableWallBeat: group.beat };
      }
      continue;
    }
    const targetY = (inter.min + inter.max) / 2;
    try {
      const segment = planSegmentInputs(
        config,
        stub,
        lastBeat,
        group.beat,
        curY,
        curPressing,
        targetY,
      );
      curPressing = segment.endPressing;
    } catch (err) {
      if (hasWall) {
        return { kept: out, unreachableWallBeat: group.beat };
      }
      // Planner failed mid-segment (typically a bracket-search non-
      // convergence on a borderline-reachable target). Drop the group's
      // obstacles — but log so future "where did my obstacles go?"
      // debugging has a trail.
      console.warn(
        `[wavesFromCircles] dropped obstacles ${obstaclesInGroup.map((o) => o.id).join(',')} at beat ${group.beat}: ${err instanceof Error ? err.message : err}`,
      );
      continue;
    }
    out.push(...obstaclesInGroup);
    lastBeat = group.beat;
    curY = targetY;
  }

  return { kept: out, unreachableWallBeat: null };
}

/**
 * Thread the chart and self-heal any wall the greedy planner can't reach.
 *
 * The corridor BOTH rolls and breathes, so a meander + an intervening
 * obstacle can occasionally pin the ship where the next wall's narrow
 * clear band is out of single-flip range. Rather than ship an unclearable
 * wall (the perfect-replay engine would die on it) OR drop the wall's
 * entire downstream tail (the old freeze-the-cursor behaviour, which
 * collapsed dense charts to almost nothing), we melt that one wall into a
 * glide-able ramp and re-thread. Healing only ever REMOVES a wall, so the
 * pass is monotonic and bounded by the wall count — it always converges on
 * a fully-threadable corridor while keeping every wall it CAN justify.
 *
 * Returns the surviving obstacles AND the (possibly healed) terrain; the
 * caller must ship the returned terrain so chart, filter, and perfect
 * replay all agree on the same corridor.
 */
function filterUnreachableObstacles(
  obstacles: ReadonlyArray<WavesObstacle>,
  terrain: WavesTerrain,
  playerVyPerBeat: number,
  playerRadius: number,
  spawnY: number,
  totalBeats: number,
): { obstacles: WavesObstacle[]; terrain: WavesTerrain } {
  let workingTerrain = terrain;
  // Each healed wall melts its run-in step into a ramp over the preceding
  // cadence, re-shaping the corridor on `(beat - cadence, beat)`; obstacles
  // there get voided so none ships mounted to terrain that moved.
  const healedRampZones: Array<readonly [number, number]> = [];
  // +1 so the final all-clear pass runs after the last possible heal.
  const budget = terrainWalls(terrain).length + 1;
  for (let i = 0; i < budget; i += 1) {
    const pass = threadObstaclesOnce(
      obstacles,
      workingTerrain,
      playerVyPerBeat,
      playerRadius,
      spawnY,
      totalBeats,
      healedRampZones,
    );
    if (pass.unreachableWallBeat === null) {
      return { obstacles: pass.kept, terrain: workingTerrain };
    }
    healedRampZones.push([
      pass.unreachableWallBeat - WAVES_TERRAIN_CADENCE_BEATS,
      pass.unreachableWallBeat,
    ]);
    workingTerrain = healWallAtBeat(workingTerrain, pass.unreachableWallBeat);
  }
  // Monotonic healing guarantees we exit through the loop; this is a
  // defensive backstop (and a clear signal if that invariant ever breaks).
  const finalPass = threadObstaclesOnce(
    obstacles,
    workingTerrain,
    playerVyPerBeat,
    playerRadius,
    spawnY,
    totalBeats,
    healedRampZones,
  );
  if (finalPass.unreachableWallBeat !== null) {
    console.warn(
      `[wavesFromCircles] wall at beat ${finalPass.unreachableWallBeat} still unreachable after exhausting heal budget`,
    );
  }
  return { obstacles: finalPass.kept, terrain: workingTerrain };
}

/** True for a single-sided spike gate (the only obstacle the zigzag re-sides). */
function isSpikeGate(obs: WavesObstacle): obs is WavesGate {
  return (
    obs.kind === 'gate' &&
    (obs.visual === 'spike-floor' || obs.visual === 'spike-ceiling')
  );
}

function spikeSideOf(gate: WavesGate): SpikeSide {
  return gate.visual === 'spike-floor' ? 'floor' : 'ceiling';
}

/** Centre of a spike's safe band for a given side (built from the same geometry). */
function spikeBandCentre(
  beat: number,
  side: SpikeSide,
  variant: Variant,
  terrain: WavesTerrain,
): number {
  const g = makeSpikeGate('probe', beat, side, variant, terrain);
  return (g.gapTopY + g.gapBottomY) / 2;
}

/** Minimum spikes in a row (within the cadence) before the zigzag engages. */
const SAWTOOTH_MIN_RUN = 3;

/**
 * Longest gap (beats) two consecutive spikes can have and still count as
 * the same "run". Spikes spaced wider than this keep their authored osu
 * side — the zigzag only re-shapes dense runs into a readable corridor,
 * leaving sparse / expressive single spikes on their musical placement.
 */
const SAWTOOTH_MAX_CADENCE_BEATS = 2.0;

/**
 * Reachability slack (playfield units) the zigzag keeps in hand when
 * deciding whether to flip sides, so the downstream reachability filter
 * almost never has to drop a re-sided spike (which would thin density and
 * risk the monotonic-difficulty invariant). The filter is still the hard
 * backstop — this just keeps the two in lockstep.
 */
const SAWTOOTH_REACH_MARGIN = 12;

/**
 * Re-side dense spike RUNS into a clean alternating floor↔ceiling zigzag —
 * the signature Geometry-Dash–wave "trace the 45° corridor" pattern. A
 * floor spike pins the safe band to the ceiling (climb) and a ceiling
 * spike pins it to the floor (dive), so alternating them turns a run into a
 * hold-release-hold sawtooth the player flies along; where spikes cluster
 * too tightly to flip, the side simply holds, which reads instead as a
 * long-hold→long-release ceiling/floor alternation (also a wave staple).
 *
 * Count-neutral by construction: it only RE-SIDES existing spikes (never
 * adds or drops), and it only flips a side when (a) the gap clears
 * {@link OPPOSING_MIN_BEAT_GAP} (so no sub-threshold opposing pair is
 * created that the engine would read as a merged hazard) and (b) the flip
 * is reachable from the previous spike's band at the variant's vertical
 * speed (with {@link SAWTOOTH_REACH_MARGIN} slack). Sparse spikes and
 * centred slider/spinner tunnels are untouched. Runs BEFORE {@link injectSaws}
 * so saws inherit the zigzag's pin direction, and before the reachability
 * filter so any borderline re-side is still validated end-to-end.
 */
export function injectSawtoothRuns(
  obstacles: ReadonlyArray<WavesObstacle>,
  variant: Variant,
  terrain: WavesTerrain,
): WavesObstacle[] {
  const vY = WAVES_VARIANT_TUNING[variant].vYPerBeat;
  const result = [...obstacles];
  let i = 0;
  while (i < result.length) {
    const head = result[i];
    if (!isSpikeGate(head)) {
      i += 1;
      continue;
    }
    // Extend the run over consecutive spikes within the cadence.
    let end = i;
    while (
      end + 1 < result.length &&
      isSpikeGate(result[end + 1]) &&
      result[end + 1].beat - result[end].beat <= SAWTOOTH_MAX_CADENCE_BEATS
    ) {
      end += 1;
    }
    if (end - i + 1 >= SAWTOOTH_MIN_RUN) {
      // Anchor on the first spike's authored side, then alternate down the
      // run wherever a flip is both spacing-safe and reachable.
      let prevSide = spikeSideOf(head);
      let prevCentre = spikeBandCentre(head.beat, prevSide, variant, terrain);
      for (let k = i + 1; k <= end; k += 1) {
        const gate = result[k] as WavesGate;
        const gap = gate.beat - result[k - 1].beat;
        const flipSide: SpikeSide = prevSide === 'floor' ? 'ceiling' : 'floor';
        const flipCentre = spikeBandCentre(gate.beat, flipSide, variant, terrain);
        const canFlip =
          gap >= OPPOSING_MIN_BEAT_GAP &&
          vY * gap >= Math.abs(flipCentre - prevCentre) + SAWTOOTH_REACH_MARGIN;
        const side = canFlip ? flipSide : prevSide;
        result[k] = makeSpikeGate(gate.id, gate.beat, side, variant, terrain);
        prevSide = side;
        prevCentre = canFlip
          ? flipCentre
          : spikeBandCentre(gate.beat, side, variant, terrain);
      }
    }
    i = end + 1;
  }
  return result;
}

/**
 * Reforge a deterministic subset of spike gates into beat-keyed saws,
 * mounted flush to a surface OR floated centre-corridor as a pick-a-side
 * commitment.
 *
 * A spike forces the player to pin to one surface to clear a flat gap; a
 * saw demands the same pin but you must arc your circular body AROUND a
 * circular blade. Two flavours, both replay-deterministic:
 *
 *   - **Mounted** (default): the blade sits on the surface the spike's
 *     lethal pillar rose from, so the pin DIRECTION is preserved — a floor
 *     spike (forces a climb) becomes a floor-mounted saw (still forces a
 *     climb), letting saws and the surviving spikes intermix without
 *     inverting the read. A beat-keyed sine bobs it deeper / shallower
 *     along a run for a "rotating gauntlet" feel while staying on its safe
 *     side (so it never closes the open lane past the minimum clearance).
 *   - **Central** (`centralProb`, only where the corridor is wide enough
 *     to leave a roundable lane on BOTH sides): the blade floats near the
 *     corridor midline with a gentle beat-keyed weave, so the player must
 *     commit above OR below it — the iconic GD-wave "pick a side past the
 *     disc" beat. Both lanes stay open, so the reachability planner threads
 *     whichever is wider and the pass is always clearable; the weave just
 *     biases which side reads as the intended line, and a run of them
 *     alternates into an over/under weave.
 *
 * The blade radius scales with the LOCAL corridor (so a squeezed stretch
 * gets smaller saws or none). Deterministic: seeded PRNG
 * (`slug:variant:saws`) + sine sampled at the fixed hazard beat. Runs
 * BEFORE the reachability filter, which silently drops any saw a squeeze
 * leaves no lane around — so the corridor stays threadable and the
 * perfect-replay planner clears every survivor.
 */
function injectSaws(
  obstacles: ReadonlyArray<WavesObstacle>,
  terrain: WavesTerrain,
  variant: Variant,
  seed: string,
): WavesObstacle[] {
  const tuning = WAVES_SAW_TUNING[variant];
  const playerRadius = WAVES_VARIANT_TUNING[variant].playerRadius;
  const rng = mulberry32(hashStringToSeed(`${seed}:${variant}:saws`));
  let lastSawBeat = -Infinity;
  return obstacles.map((obs) => {
    // Only spikes convert: a centred tunnel pillar can't be expressed as a
    // single-sided saw, and leaving them keeps the slider/spinner "thread
    // the middle" identity intact.
    if (
      obs.kind !== 'gate' ||
      (obs.visual !== 'spike-floor' && obs.visual !== 'spike-ceiling')
    ) {
      return obs;
    }
    if (obs.beat - lastSawBeat < tuning.minSpacingBeats || rng() >= tuning.convertProb) {
      return obs;
    }
    // Draw the central-vs-mounted roll HERE, before the float corridor-width
    // maths below, so the PRNG stream position depends only on WHICH spikes
    // convert (a beat-spacing + convertProb decision) — never on a
    // float-threshold comparison of terrain-derived widths. That keeps the
    // client and server saw sequences bit-identical even if the terrain
    // arithmetic ever shifts.
    const centralRoll = rng();
    const ceil = ceilingYAt(terrain, obs.beat);
    const flr = floorYAt(terrain, obs.beat);
    const width = flr - ceil;
    const radius = clamp(width * tuning.radiusFrac, tuning.minRadius, tuning.maxRadius);
    // Lane left for the player once the blade is mounted at its base
    // depth. Too tight (a squeeze) → keep the spike instead.
    const openLane = width - 2 * radius - 2 * playerRadius;
    if (openLane < WAVES_SAW_MIN_OPEN_CLEARANCE) {
      return obs;
    }
    // A central blade splits the open lane in two, so each side must keep the
    // player's body PLUS the minimum clearance — the same effective margin a
    // mounted blade's single open lane keeps. Where there's that much room,
    // float it at the per-variant `centralProb`; otherwise the blade mounts
    // flush to its pin surface as before.
    const halfLane = openLane / 2;
    const canCentral = halfLane >= playerRadius + WAVES_SAW_MIN_OPEN_CLEARANCE;
    const central = canCentral && centralRoll < tuning.centralProb;
    let centerY: number;
    if (central) {
      // Weave around the midline, but never enough to pinch either lane's
      // clearance below the minimum — both stay roundable (player body plus
      // margin), so the planner always has a valid side and consecutive
      // blades read as an over/under weave rather than a hard wall.
      const mid = (ceil + flr) / 2;
      const maxBias = halfLane - playerRadius - WAVES_SAW_MIN_OPEN_CLEARANCE;
      const bias = clamp(tuning.oscAmplitude * Math.sin(obs.beat * tuning.oscFreq), -maxBias, maxBias);
      centerY = mid + bias;
    } else {
      const mountFloor = obs.visual === 'spike-floor';
      const baseCentre = mountFloor ? flr - radius : ceil + radius;
      // Bob toward the open lane, but never enough to close it past the
      // minimum clearance (so every emitted saw stays roundable).
      const maxOsc = openLane - WAVES_SAW_MIN_OPEN_CLEARANCE;
      const osc = Math.min(
        tuning.oscAmplitude * 0.5 * (1 + Math.sin(obs.beat * tuning.oscFreq)),
        maxOsc,
      );
      centerY = mountFloor ? baseCentre - osc : baseCentre + osc;
    }
    lastSawBeat = obs.beat;
    return { id: obs.id, kind: 'saw', beat: obs.beat, centerY, radius };
  });
}

/** True for a centred slider/spinner tunnel gate (the only obstacle the weave shifts). */
function isPillarGate(obs: WavesObstacle): obs is WavesGate {
  return obs.kind === 'gate' && obs.visual === 'pillar';
}

/** Minimum consecutive tunnel gates before the weave engages. */
const WEAVE_MIN_RUN = 4;

/** Longest gap (beats) two tunnel gates can have and still count as one run. */
const WEAVE_MAX_GAP_BEATS = 1.6;

/**
 * Below this intensity a tunnel run stays dead-centre — the "thread the
 * middle" read belongs to the song's gentle stretches; the weave is a
 * climax-side embellishment.
 */
const WEAVE_INTENSITY_GATE = 0.45;

/** Largest midline offset (playfield units) a weave commits to at full intensity. */
const WEAVE_MAX_AMPLITUDE = 70;

/** Below this much corridor room past the gap, a tunnel stays centred. */
const WEAVE_MIN_ROOM = 10;

/** Radians of phase advanced per gate — a quarter turn = a 4-gate over/under period. */
const WEAVE_PHASE_STEP = Math.PI / 2;

/**
 * Reachability slack (playfield units) the weave keeps in hand so a
 * shifted gate stays within a single flip of the previous one — the
 * downstream reachability filter then almost never has to drop a weaved
 * tunnel (which would thin the run).
 */
const WEAVE_REACH_MARGIN = 18;

/**
 * Weave dense slider/spinner tunnel RUNS off the corridor midline into an
 * over/under S-carve, instead of the dead-centre pillar string they'd
 * otherwise be — the Geometry-Dash "alternating-height gate" beat that
 * makes the ship sweep a big S rather than hold the centre.
 *
 * Count-neutral by construction (it only shifts each gate's gap centre,
 * never adds or drops): the first gate of a run stays centred as an
 * anchor, then each subsequent gate offsets along a sine. Each offset is
 * bounded three ways so the run stays clearable and the monotonic
 * difficulty curve holds: by the LOCAL corridor room a tight gap leaves
 * ({@link WEAVE_MIN_ROOM}), by the song's intensity (gentle stretches stay
 * centred — {@link WEAVE_INTENSITY_GATE}), and by single-flip reach from
 * the previous gate ({@link WEAVE_REACH_MARGIN}). Runs before the
 * reachability filter, which is still the hard backstop for any borderline
 * shift.
 */
export function injectTunnelWeave(
  obstacles: ReadonlyArray<WavesObstacle>,
  terrain: WavesTerrain,
  variant: Variant,
  totalBeats: number,
): WavesObstacle[] {
  const vY = WAVES_VARIANT_TUNING[variant].vYPerBeat;
  const result = [...obstacles];
  let i = 0;
  while (i < result.length) {
    if (!isPillarGate(result[i])) {
      i += 1;
      continue;
    }
    // Extend the run over consecutive tunnel gates within the cadence.
    let end = i;
    while (
      end + 1 < result.length &&
      isPillarGate(result[end + 1]) &&
      result[end + 1].beat - result[end].beat <= WEAVE_MAX_GAP_BEATS
    ) {
      end += 1;
    }
    if (end - i + 1 >= WEAVE_MIN_RUN) {
      const anchor = result[i] as WavesGate;
      let prevCentre = (anchor.gapTopY + anchor.gapBottomY) / 2;
      for (let k = i + 1; k <= end; k += 1) {
        const gate = result[k] as WavesGate;
        const halfGap = (gate.gapBottomY - gate.gapTopY) / 2;
        const ceil = ceilingYAt(terrain, gate.beat);
        const flr = floorYAt(terrain, gate.beat);
        const mid = (ceil + flr) / 2;
        const intensity = wavesIntensityAt(gate.beat, totalBeats);
        const room = (flr - ceil) / 2 - halfGap - PLANNER_EDGE_MARGIN;
        if (intensity < WEAVE_INTENSITY_GATE || room < WEAVE_MIN_ROOM) {
          prevCentre = mid;
          continue;
        }
        const amp = Math.min(intensity * WEAVE_MAX_AMPLITUDE, room);
        const sineTarget = mid + amp * Math.sin((k - i) * WEAVE_PHASE_STEP);
        // Compose BOTH bounds at once — single-flip reach from the previous
        // gate AND the local corridor — so a corridor clamp can't silently
        // push the gate past single-flip range (which the filter would then
        // drop, thinning the run). The window contains `prevCentre` whenever
        // it sits in this beat's corridor; if the corridor has drifted off it
        // (empty window) snap to the nearest corridor edge as a best effort
        // and let the reachability filter arbitrate.
        const reach = Math.max(0, vY * (gate.beat - result[k - 1].beat) - WEAVE_REACH_MARGIN);
        const lo = Math.max(prevCentre - reach, ceil + halfGap);
        const hi = Math.min(prevCentre + reach, flr - halfGap);
        const centre =
          lo <= hi ? clamp(sineTarget, lo, hi) : clamp(prevCentre, ceil + halfGap, flr - halfGap);
        result[k] = { ...gate, gapTopY: centre - halfGap, gapBottomY: centre + halfGap };
        prevCentre = centre;
      }
    }
    i = end + 1;
  }
  return result;
}

export function buildObstaclesForVariant(
  variant: Variant,
  bundle: ImportedCircleBundle,
  totalBeats: number,
  terrain: WavesTerrain,
): WavesObstacle[] {
  const allHits = normaliseHits(bundle.circles).sort((a, b) => a.beat - b.beat);
  const filtered = wavesIntensityFilter(variant, allHits);
  const obstacles: WavesObstacle[] = [];
  let nextId = 0;
  const makeId = (prefix: string): string => `${prefix}-${(nextId++).toString().padStart(4, '0')}`;

  for (const h of filtered) {
    if (h.beat < 0 || h.beat > totalBeats) {
      continue;
    }
    if (h.kind === 'circle') {
      const side = spikeSideFromOsuY(h.y);
      obstacles.push(makeSpikeGate(makeId('w'), h.beat, side, variant, terrain));
      continue;
    }
    if (h.kind === 'slider' && h.endBeat !== undefined) {
      const endBeat = Math.min(h.endBeat, totalBeats);
      const step = SLIDER_TUNNEL_BEAT_STEP[variant];
      let t = h.beat;
      // Head + intermediate tunnel gates + tail. Always emit at least the
      // head so a slider doesn't disappear when its span is shorter than
      // the step (which can happen on very fast sliders).
      obstacles.push(makeTunnelGate(makeId('s'), t, SLIDER_GAP_MUL, variant, terrain));
      t += step;
      while (t < endBeat - step * 0.5) {
        obstacles.push(makeTunnelGate(makeId('s'), t, SLIDER_GAP_MUL, variant, terrain));
        t += step;
      }
      if (endBeat > h.beat) {
        obstacles.push(makeTunnelGate(makeId('s'), endBeat, SLIDER_GAP_MUL, variant, terrain));
      }
      continue;
    }
    if (h.kind === 'spinner' && h.endBeat !== undefined) {
      const endBeat = Math.min(h.endBeat, totalBeats);
      const step = SPINNER_TUNNEL_BEAT_STEP[variant];
      // Spinner = tightest tunnel section: centred gates only. Saws mount
      // on ONE surface (see `injectSaws`), so they only reforge single-
      // sided spikes — a centred spinner tunnel keeps its "thread the
      // middle" identity rather than becoming a side-gap saw.
      let t = h.beat;
      obstacles.push(makeTunnelGate(makeId('p'), t, SPINNER_GAP_MUL, variant, terrain));
      t += step;
      while (t < endBeat - step * 0.5) {
        obstacles.push(makeTunnelGate(makeId('p'), t, SPINNER_GAP_MUL, variant, terrain));
        t += step;
      }
      if (endBeat > h.beat) {
        obstacles.push(makeTunnelGate(makeId('p'), endBeat, SPINNER_GAP_MUL, variant, terrain));
      }
      continue;
    }
  }

  const sorted = obstacles.sort((a, b) => a.beat - b.beat);
  return sorted;
}

export interface AssembleWavesArgs {
  baseSlug: string;
  baseTitle: string;
  description: string;
  difficulty: Difficulty;
  palette: ChartPalette;
  audioSrc: string;
  attribution: string;
  mapper: string;
  /** See {@link import('../circlesBuiltinMaps/importedOsuChart.js').AssembleImportedCirclesArgs.anchorOnsets}. */
  anchorOnsets: ImportedOnsetsFile;
  /** See {@link import('../circlesBuiltinMaps/importedOsuChart.js').AssembleImportedCirclesArgs.imported}. */
  imported: Record<Variant, ImportedCircleBundle>;
  /** See {@link import('../circlesBuiltinMaps/importedOsuChart.js').AssembleImportedCirclesArgs.timing}. */
  timing: Record<Variant, PerVariantTimingArg>;
}

/**
 * Build all three WavesChart variants from a track's Circles
 * imported-JSON bundles. The derivation is fully deterministic — the
 * same input bundles always produce identical obstacle lists, which
 * lets server-side replay validation re-derive the chart instead of
 * persisting a full obstacle blob per map.
 *
 * Each variant uses its own `(bpm, offsetMs, totalBeats)` timing
 * triple and its own osu hit-object bundle, so harder variants are
 * genuinely denser charts (osu mappers author distinct diffs), not
 * just a global density scalar.
 */
export function assembleWavesChartFromCircles(
  args: AssembleWavesArgs,
): Record<Variant, WavesChart> {
  const {
    baseSlug,
    baseTitle,
    description,
    difficulty,
    palette,
    audioSrc,
    attribution,
    mapper,
    anchorOnsets,
    imported,
    timing,
  } = args;

  const variants: Variant[] = ['normal', 'hard', 'extreme'];
  const out = {} as Record<Variant, WavesChart>;
  for (const variant of variants) {
    const t = timing[variant];
    const tuning = WAVES_VARIANT_TUNING[variant];
    const audio: ChartAudio = {
      src: audioSrc,
      bpm: t.bpm,
      offsetMs: t.offsetMs,
      durationSec: anchorOnsets.durationSec,
      attribution,
      onsets: rescaleOnsets(
        anchorOnsets.bpm,
        anchorOnsets.offsetMs,
        anchorOnsets.onsets,
        t.bpm,
        t.offsetMs,
      ),
    };
    // Cap chart wall-time to audio duration so the music can never
    // end before the chart does. The declared `totalBeats` is the
    // mapper's intent; the audio is the source of truth. Without
    // this cap, post-music silence creeps in whenever beat-domain
    // intent overshoots the actually-cut audio file by even a
    // fraction of a second.
    const audioBeats = (audio.durationSec * t.bpm) / 60;
    const totalBeats = Math.min(t.totalBeats, Math.floor(audioBeats));
    // Winding-corridor terrain comes first — spikes and tunnel gates
    // anchor to its local surface, and walls become mandatory waypoints
    // the reachability filter must thread.
    const terrain = generateWavesTerrain(baseSlug, variant, totalBeats, tuning.vYPerBeat);
    // Backfill long empty stretches with the same beat-grid filler helper
    // Circles uses (`withFillerCircleEntries`), so a track's intro /
    // breakdowns aren't dead corridor. This variant feeds it its own
    // same-named diff, so the filler matches the diff Waves derives from
    // (Circles tier-shifts, so it needn't match the same-named Circles
    // tier). Filler circles become spike obstacles via the normal
    // derivation; unreachable ones are dropped by the reachability pass
    // below, so the corridor stays threadable.
    const filledBundle: ImportedCircleBundle = {
      ...imported[variant],
      circles: withFillerCircleEntries(imported[variant].circles, totalBeats),
    };
    const rawObstacles = buildObstaclesForVariant(variant, filledBundle, totalBeats, terrain);
    // Re-side dense spike runs into a traceable floor↔ceiling zigzag (the
    // signature wave "fly the 45° corridor" pattern). The transform itself
    // adds/drops nothing (re-sides only); the reachability filter below
    // stays the authority on final density across variants, so the monotonic
    // difficulty curve holds. Runs first so saws inherit the zigzag's pin
    // direction.
    const zigzagged = injectSawtoothRuns(rawObstacles, variant, terrain);
    // Reforge a deterministic subset of spikes into saws (mixed in with
    // the survivors) before the runway + reachability passes, so saws are
    // wall-spaced and reachability-checked exactly like spikes.
    const withSaws = injectSaws(zigzagged, terrain, variant, baseSlug);
    // Weave dense tunnel runs off-centre into an over/under S-carve on the
    // song's higher-intensity stretches (count-neutral; gentle stretches
    // stay centred). Runs before the runway + reachability passes so weaved
    // gates are reachability-checked exactly like every other obstacle.
    const weaved = injectTunnelWeave(withSaws, terrain, variant, totalBeats);
    // Clear each wall's approach/escape runway before the (expensive)
    // reachability pass so walls keep the room they need to stay
    // threadable.
    const nearWallFiltered = dropObstaclesNearWalls(weaved, terrain, tuning.vYPerBeat);
    // Threads the corridor and melts any unreachable wall into a ramp, so
    // the shipped terrain may differ from the generated one — use the
    // returned terrain everywhere downstream.
    const { obstacles, terrain: threadedTerrain } = filterUnreachableObstacles(
      nearWallFiltered,
      terrain,
      tuning.vYPerBeat,
      tuning.playerRadius,
      WAVES_DEFAULT_SPAWN_Y,
      totalBeats,
    );
    const chart: WavesChart = {
      mode: 'waves',
      id: variant === 'normal' ? baseSlug : `${baseSlug}-${variant}`,
      slug: variant === 'normal' ? baseSlug : `${baseSlug}-${variant}`,
      title: buildVariantTitle(baseTitle, variant),
      description,
      bpm: t.bpm,
      totalBeats,
      difficulty,
      variant,
      palette,
      audio,
      mapper,
      spawnY: WAVES_DEFAULT_SPAWN_Y,
      playerVyPerBeat: tuning.vYPerBeat,
      playerRadius: tuning.playerRadius,
      obstacles,
      terrain: threadedTerrain,
    };
    out[variant] = chart;
  }
  return out;
}

// Re-exported for the test that builds the same skeleton from a
// hand-written hit list (per-osu-kind derivation test).
export type { NormalisedHit };
